export const DISTANCES = [
  { series: "Quantum Sun Series", veg: "50–60 cm · 50% power", bloom: "50–60 cm · 100% power" },
  { series: "Optic Sun 1", veg: "40–50 cm", bloom: "30–40 cm" },
  { series: "StarShip Series", veg: "50–60 cm · main dial 50%, IR on", bloom: "50–60 cm · 100%, IR + UV on" },
  { series: "StarFighter", veg: "55–65 cm · 50% power", bloom: "55–65 cm · 100% power" },
  { series: "CommandShip Series", veg: "50–60 cm · veg channel 100%", bloom: "50–60 cm · all 3 channels 100%" },
  { series: "Quantum Star Series", veg: "50–60 cm · 50% power", bloom: "50–60 cm · 100% power" },
];

export const ARTICLES = [
  {
    slug: "true-watts-of-led-grow-lights",
    chapter: "Guide 01",
    kind: "Tech knowledge",
    icon: "BookOpen",
    title: "Keeping It Real — The True Watts of LED Grow Lights",
    date: "March 2020",
    excerpt: "Why quoted wattage on grow lights is so often fiction — and what the number on the box actually tells you.",
    description: "The truth about LED grow light wattage: why manufacturers exaggerate watts, how the chip-count formula misleads buyers, and why real power draw matters more than claimed numbers.",
    paragraphs: [
      "Right from the inception of LED grow lights, manufacturers and sellers have exaggerated wattage — mostly to claim they replace a HID light of some heroic number. Watts measure electricity consumption, not light output; it's like stating a car's top speed by quoting its fuel consumption.",
      "The old formula multiplied chip count by chip rating: 100 × 5W chips = a '500 watt' light. But chips run at around half their maximum rating, so that 500W light really drew about 250W. In recent years even that discipline has gone — I've tested lights drawing less than 15% of their claimed wattage.",
      "High-performance LED grow lights have now come of age: the good ones outperform HPS using around half the watts. The exaggerators are usually the sellers of average lights. If you're shopping, always find out the real power draw — an inflated number tells you nothing about output, only that the marketing is doing the heavy lifting. Happy growing.",
    ],
  },
  {
    slug: "grow-room-ventilation",
    chapter: "Guide 02",
    kind: "Tech knowledge",
    icon: "Wind",
    title: "Ventilation in the Grow Room",
    date: "November 2017",
    excerpt: "Air exchange, CO2 and temperature — the invisible third of your grow that decides whether genetics shine.",
    description: "Grow room ventilation explained for home growers: how much air exchange plants need, CO2 levels and growth, where to mount exhaust and intakes, and avoiding air channelling.",
    paragraphs: [
      "Plants need CO2 to grow, and the air around us holds about 400ppm. Without adequate exchange, levels fall — growth slows and stalls near 200ppm. Ventilation also removes heat produced by lighting; the sweet spot in a grow room is 24–28°C.",
      "Aim to replace the full volume of air in your space every 20 minutes with a properly sized exhaust fan. Mount exhaust high where hot air pools, and place intakes low on the opposite side to draw in cool, fresh air.",
      "An oscillating fan in front of the intakes spreads fresh air evenly and prevents 'air channelling', where some plants get more CO2 than others. And make sure exhausted air can't loop straight back into the room — intake and exhaust should draw from and dump to different areas.",
    ],
  },
  {
    slug: "sealed-environment-co2",
    chapter: "Guide 03",
    kind: "Tech knowledge",
    icon: "CloudCog",
    title: "Sealed Environments with CO2 Enhancement",
    date: "May 2023",
    excerpt: "The highest-performance way to grow: seal the room, control the climate, and feed the plants extra CO2.",
    description: "How sealed grow rooms with CO2 enhancement work: shorter veg and flower cycles, ~30% yield increases, pest and mould control, and why sealed setups can cut energy costs.",
    paragraphs: [
      "A sealed environment with CO2 enhancement is the highest-performance way to grow. Seal the room, add adequate air conditioning and dehumidification, and pair a CO2 controller with a regulator, solenoid and bottle (warmer climates) or a CO2 burner (cooler ones).",
      "The payoff is serious: veg time drops from around 4 weeks to 3, flowering from 8 weeks to 6.5–7, and yields climb by roughly 30%. A sealed room also keeps pests and fungal problems like grey mould, botrytis and bud rot at bay, and odour control is as simple as a fan with a carbon filter.",
      "Because you're not constantly exhausting conditioned air, a sealed setup can even cut energy costs — your appliances stop fighting the outside world, and your plants get a climate better than nature can supply.",
    ],
  },
  {
    slug: "led-hanging-distance-guide",
    chapter: "Guide 04",
    kind: "Tech knowledge",
    icon: "Ruler",
    title: "Correct Light Hanging Distance (All Series)",
    date: "Tech sheet",
    excerpt: "The exact veg and bloom hanging distances for every SuperTechLED series — plus how to read stretch and saturation.",
    description: "Recommended LED grow light hanging distances for veg and flowering across Quantum Sun, Optic Sun, StarShip, StarFighter, CommandShip and Quantum Star — with troubleshooting for stretch and light bleaching.",
    paragraphs: [
      "A rough guide that works well — growers can push results further by adjusting to their own environment. Seedlings and clones need less: dial down or raise the light to get them started.",
      "The symptoms tell you everything. Too little light and plants stretch, growing leggy with long internodes. Too much and growth slows — and at the extreme you'll see light bleaching on the top colas. To cover a bigger vegetative footprint, raise the light and increase intensity.",
    ],
    table: true,
  },
  {
    slug: "reduce-watering-under-led",
    chapter: "Expert tip 01",
    kind: "Expert tip",
    icon: "Droplets",
    title: "Reduce Watering Under LED Grow Lights",
    date: "Quick tip",
    excerpt: "LEDs run cooler than HID — which means your plants drink less. Here's how much less.",
    description: "Plants under LED grow lights need roughly half the watering of HID setups. Learn why lower heat means slower uptake, and how to adjust your watering schedule.",
    paragraphs: [
      "Plants under LED grow lights need less water than under HID lights like HPS or MH. Cut watering to roughly half of your HID routine — if you'd normally water 6 times a day in a given system and medium, drop to 3 under LED. Less heat means less evaporation and slower uptake.",
      "Over-watering under LED is one of the most common mistakes growers make when switching from HPS. Watch the medium, not the calendar: let it approach dry-back before the next feed, and your roots will thank you with faster, healthier growth.",
    ],
  },
  {
    slug: "increase-nutrient-strength-led",
    chapter: "Expert tip 02",
    kind: "Expert tip",
    icon: "FlaskConical",
    title: "Increase Nutrient Strength Under LED",
    date: "Quick tip",
    excerpt: "Less watering doesn't mean less feeding — bump your nutrient strength to compensate.",
    description: "Why you should increase nutrient strength by 20–50% when growing under LED: plants drink less often but still need the same total nutrition.",
    paragraphs: [
      "Even though they drink less, plants under LED still need the same total nutrition. Bump nutrient strength by 20–50% — the reduced watering frequency means fewer feeds, so the extra strength compensates and keeps growth charging.",
      "Start at the lower end of the increase and watch the leaf tips. Slight tip burn means you've found the ceiling — back off 10% and you've dialled in your mix.",
    ],
  },
  {
    slug: "dial-in-hanging-distance",
    chapter: "Expert tip 03",
    kind: "Expert tip",
    icon: "Ruler",
    title: "Dial In Your Hanging Distance",
    date: "Quick tip",
    excerpt: "Too far and plants stretch; too close and growth stalls. Finding the sweet spot is free yield.",
    description: "How to find the optimum hanging distance for your LED grow light in veg and bloom — avoid stretch from too little light and saturation shutdown from too much.",
    paragraphs: [
      "Too far from the canopy and plants stretch and yield poorly. Too close and they hit light saturation point, shutting growth down. Learn the optimum distance for your light in both veg and bloom — and keep it there as the canopy rises.",
      "Check distances weekly: a plant that grew 5cm toward the light just changed its own PPFD. Our per-series distance chart covers every SuperTechLED fixture — see the hanging distance guide for exact numbers.",
    ],
    linkTo: "led-hanging-distance-guide",
  },
];
